import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { getClosestContainerNode, getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';
import { stripTags } from '../../utils/ids-xss-utils/ids-xss-utils';

import IdsToolbarSection from './ids-toolbar-section';
import './ids-toolbar-more-actions';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-toolbar.scss';
import type IdsToolbarMoreActions from './ids-toolbar-more-actions';
import IdsSearchField from '../ids-search-field/ids-search-field';

const FORMATTER_VARIANT = 'alternate-formatter';
const TOOLBAR_TYPES = ['formatter'];

interface ToolbarItem extends HTMLElement {
  disabled?: boolean;
  tabIndex: number;
  value?: any;
  overflowTarget?: HTMLElement;
}

const Base = IdsKeyboardMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS Toolbar Component
 * @type {IdsToolbar}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsKeyboardMixin
 */
@customElement('ids-toolbar')
@scss(styles)
export default class IdsToolbar extends Base {
  /**
   * Watches for Toolbar size changes
   */
  #resizeObserver?: ResizeObserver | null;

  /**
   * Watches for DOM changes in tabs
   */
  #mutationObserver?: MutationObserver | null;

  /**
   * Flag to prevent recursive calls during overflow refresh
   */
  #isRefreshing = false;

  constructor() {
    super();
  }

  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.DISABLED,
      attributes.PADDING,
      attributes.TABBABLE,
      attributes.TYPE
    ];
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.setAttribute('role', 'toolbar');
    this.#attachEventHandlers();
    this.#attachKeyboardListeners();
    this.#configureResizeObserver();
    this.#configureMutationObserver();
    this.#setType(null, this.type);
    this.makeTabbable(this.detectTabbable());

    if (this.hasAttribute(attributes.PADDING)) {
      this.padding = this.getAttribute(attributes.PADDING) as string;
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
  }

  /**
   * Configures the resize observer.
   * @private
   */
  #configureResizeObserver(): void {
    this.#resizeObserver = new ResizeObserver(() => {
      this.#resize();
    });
    this.#resizeObserver.observe(this);
  }

  /**
   * Configures the mutation observer for toolbar content changes.
   * @private
   */
  #configureMutationObserver(): void {
    this.#mutationObserver = new MutationObserver((mutations) => {
      // Prevent recursive calls during refresh operations
      if (this.#isRefreshing) return;

      // Filter out mutations that occur within ids-toolbar-more-actions to avoid infinite loops
      const relevantMutations = mutations.filter((mutation) => {
        const target = mutation.target as Element;
        // Ignore mutations inside the more-actions component
        return !target.closest('ids-toolbar-more-actions');
      });

      if (relevantMutations.length === 0) return;

      const hasNewButtons = relevantMutations.some((mutation) => mutation.type === 'childList'
        && mutation.addedNodes.length > 0
        && Array.from(mutation.addedNodes).some((node: any) => node.nodeType === Node.ELEMENT_NODE
          && node.matches?.('ids-button, ids-menu-button')));

      const hasTabChanges = relevantMutations.some((mutation) => mutation.type === 'childList');

      // Check for menu content changes (menu items added/removed) in toolbar buttons' menus
      const hasMenuContentChanges = relevantMutations.some((mutation) => mutation.type === 'childList'
        && mutation.target instanceof Element
        && mutation.target.matches?.('ids-popup-menu, ids-menu-group')
        // Only respond to menu changes for menu buttons in toolbar sections (not in more-actions)
        && mutation.target.closest('ids-toolbar-section'));

      const hasBasicAttributeChanges = relevantMutations.some((mutation) => mutation.type === 'attributes'
        && mutation.target instanceof Element
        && mutation.target.matches?.('ids-button, ids-menu-button')
        && ['hidden', 'disabled', 'overflow'].includes(mutation.attributeName || ''));

      // Extract style changes separately for special handling
      const styleAttributeChanges = relevantMutations.filter((mutation) => mutation.type === 'attributes'
        && mutation.target instanceof Element
        && mutation.target.matches?.('ids-button, ids-menu-button')
        && mutation.attributeName === 'style');

      // Check for attributes that need syncing to overflow items
      const syncableAttributeChanges = relevantMutations.filter((mutation) => mutation.type === 'attributes'
        && mutation.target instanceof Element
        && mutation.target.matches?.('ids-button, ids-menu-button')
        && ['icon', 'text', 'appearance', 'color-variant'].includes(mutation.attributeName || ''));

      if (hasNewButtons) {
        this.rebuildOverflowMenu();
      } else if (hasMenuContentChanges) {
        // Rebuild overflow menu when menu content changes
        this.rebuildOverflowMenu();
      } else if (styleAttributeChanges.length > 0) {
        // Style changes that affect display need to rebuild the overflow menu
        // because items with display:none are filtered from the items list
        this.rebuildOverflowMenu();
      } else if (syncableAttributeChanges.length > 0) {
        // Sync specific attributes to overflow items
        const moreActions = this.querySelector<IdsToolbarMoreActions>('ids-toolbar-more-actions');
        if (moreActions) {
          syncableAttributeChanges.forEach((mutation) => {
            const button = mutation.target as HTMLElement;
            const attributeName = mutation.attributeName || '';
            const newValue = button.getAttribute(attributeName);
            moreActions.syncAttributeToOverflow(button, attributeName, newValue);
          });
        }
      } else if (hasTabChanges || hasBasicAttributeChanges) {
        this.refreshOverflowedItems();
      }
    });

    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'disabled', 'overflow', 'icon', 'text', 'appearance', 'color-variant', 'style']
    });
  }

  /**
   * Sets up event handlers assigned to the Toolbar and its child elements
   * @private
   * @returns {void}
   */
  #attachEventHandlers(): void {
    // Translate click events on buttons into Toolbar `selected` events to correspond
    // to the behavior of menu buttons and the "More Actions" menu
    this.onEvent('click.toolbar-item', this, (e: any) => {
      const btn = e.target.closest('ids-menu-button, ids-button');
      if (btn) {
        this.triggerSelectedEvent(btn);
        this.triggerEvent('click', this, {
          bubbles: true,
          detail: { elem: btn }
        });
      }
    });

    const searchfield = this.querySelector<IdsSearchField>('ids-search-field');
    const toggleExpanded = (shouldExpand: boolean) => {
      this.sections.forEach((section) => {
        const shadowSection = section.shadowRoot?.querySelector('.ids-toolbar-section');

        if (shouldExpand) {
          shadowSection?.classList.add('searchfield-expanded');
          section?.classList.add('searchfield-expanded');
        } else {
          shadowSection?.classList.remove('searchfield-expanded');
          section?.classList.remove('searchfield-expanded');
        }
      });
    };

    if (searchfield && searchfield?.onEvent) {
      searchfield?.onEvent('expand', searchfield, () => {
        toggleExpanded(true);
      });

      searchfield?.onEvent('collapse', searchfield, () => {
        toggleExpanded(false);
      });
    }
  }

  /**
   * Sets up the connection to the global keyboard handler
   * @private
   * @returns {void}
   */
  #attachKeyboardListeners(): void {
    // Arrow Up navigates focus backward
    this.listen(['ArrowLeft'], this, (e: any) => {
      // If the target is within a search field, allow normal cursor movement
      if (e.target.closest('ids-search-field')) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // If the target is a menu item, either a menu or actions button is currently open
      // with its children focused, and navigation shouldn't continue
      if (e.target.name === 'ids-menu-item') {
        return;
      }
      this.navigate(-1, true);
    });

    // Arrow Right navigates focus forward
    this.listen(['ArrowRight'], this, (e: any) => {
      // If the target is within a search field, allow normal cursor movement
      if (e.target.closest('ids-search-field')) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // If the target is a menu item, either a menu or actions button is currently open
      // with its children focused, and navigation shouldn't continue
      if (e.target.name === 'ids-menu-item') {
        return;
      }
      this.navigate(1, true);
    });

    // Arrow Up acts as Left Arrow (navigates backward)
    this.listen(['ArrowUp'], this, (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      // If the target is a menu item, either a menu or actions button is currently open
      // with its children focused, and navigation shouldn't continue
      if (e.target.name === 'ids-menu-item') {
        return;
      }
      this.navigate(-1, true);
    });

    // Arrow Down acts as Right Arrow (navigates forward)
    this.listen(['ArrowDown'], this, (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      // If the target is a menu item, either a menu or actions button is currently open
      // with its children focused, and navigation shouldn't continue
      if (e.target.name === 'ids-menu-item') {
        return;
      }
      this.navigate(1, true);
    });
  }

  /**
   * Uses a currently-highlighted toolbar item to "navigate" a specified number
   * of steps to another toolbar item, highlighting it.
   * @param {number} [amt] the amount of items to navigate
   * @param {boolean} [doFocus] if true, causes the new item to become focused.
   * @returns {ToolbarItem} the item that will be focused
   */
  navigate(amt = 0, doFocus = false): ToolbarItem {
    const items = this.items;
    let currentItem = this.focused || items[0];

    if (typeof amt !== 'number') {
      return currentItem;
    }

    // Calculate steps/meta
    const negative = amt < 0;
    let steps = Math.abs(amt);
    let currentIndex = items.indexOf(currentItem);

    // Step through items to the target
    while (steps > 0) {
      currentItem = items[currentIndex + (negative ? -1 : 1)];
      currentIndex = items.indexOf(currentItem);

      // "-1" means we've crossed the boundary and need to loop back around
      if (currentIndex < 0) {
        currentIndex = (negative ? items.length - 1 : 0);
        currentItem = items[currentIndex];
      }

      // Don't count disabled/overflowed items as "taking a step"
      if (!currentItem.disabled && !currentItem.hasAttribute(attributes.OVERFLOWED)) {
        steps -= 1;
      }
    }

    if (!currentItem.disabled && doFocus) {
      this.makeTabbable(currentItem);
      // For segmented controls, enter from the correct end based on direction
      if (currentItem.tagName?.toLowerCase() === 'ids-segmented-control') {
        const buttons = Array.from(currentItem.querySelectorAll('ids-toggle-button')) as HTMLElement[];
        const entryButton = negative ? buttons[buttons.length - 1] : buttons[0];
        (entryButton as HTMLElement)?.focus();
      } else {
        currentItem.focus();
      }
    }

    return currentItem;
  }

  template() {
    return `<div class="ids-toolbar" role="toolbar">
      <slot></slot>
    </div>`;
  }

  /**
   * Set all buttons to disabled on the toolbar
   * @param {boolean | string} val sets the disabled state of the entire toolbar
   */
  set disabled(val: boolean | string) {
    const trueVal = stringToBool(val);

    if (trueVal) {
      this.setAttribute(attributes.DISABLED, val.toString());
    } else {
      this.removeAttribute(attributes.DISABLED);
    }

    this.container?.classList[trueVal ? 'add' : 'remove'](attributes.DISABLED);

    // Set disabled state on all relevant subcomponents
    const setDisabledState = (elem: any) => {
      const elemClassList = Array.from(elem.classList || []);

      if (elemClassList.includes('more-actions-button')) {
        const trueElem = getClosest(elem, 'ids-toolbar-more-actions');
        trueElem.disabled = trueVal;
      } else {
        elem.disabled = trueVal;
      }
    };
    this.items.forEach(setDisabledState);
    this.textElems.forEach(setDisabledState);
    this.refreshOverflowedItems();
  }

  /**
   * @returns {boolean} true if the toolbar is currently disabled
   */
  get disabled(): boolean {
    return !!this.container?.classList?.contains(attributes.DISABLED);
  }

  /**
   * Refreshes the overflow state of toolbar items
   * @returns {void}
   */
  refreshOverflowedItems(): void {
    if (this.#isRefreshing) return;

    this.#isRefreshing = true;
    try {
      const moreActions = this.querySelector<IdsToolbarMoreActions>('ids-toolbar-more-actions');
      if (moreActions) {
        moreActions.refreshOverflowedItems();
      }
    } finally {
      this.#isRefreshing = false;
    }
  }

  /**
   * Pause the mutation observer to prevent rebuilds during beforeShow
   */
  pauseMutationObserver(): void {
    this.#mutationObserver?.disconnect();
  }

  /**
   * Resume the mutation observer after beforeShow completes
   */
  resumeMutationObserver(): void {
    if (this.#mutationObserver) {
      this.#mutationObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'disabled', 'overflow', 'icon', 'text', 'appearance', 'color-variant', 'style']
      });
    }
  }

  /**
   * Refreshes the overflow text of toolbar items
   * @returns {void}
   */
  rebuildOverflowMenu(): void {
    if (this.#isRefreshing) return;

    this.#isRefreshing = true;
    try {
      const moreActions = this.querySelector<IdsToolbarMoreActions>('ids-toolbar-more-actions');
      if (moreActions) {
        moreActions.rebuildOverflowMenu();
      }
    } finally {
      this.#isRefreshing = false;
    }
  }

  /**
   * Gets the buttons in the toolbar
   * @returns {Array} Array of ids-buttons
   */
  get buttons(): Array<HTMLElement> {
    return [...this.querySelectorAll('ids-button')].filter(
      (button) => (button as HTMLElement).style.display !== 'none'
    ) as HTMLElement[];
  }

  /**
   * Gets the currently focused toolbar item
   * @readonly
   * @returns {ToolbarItem | undefined} the currently focused toolbar item, if one exists
   */
  get focused(): ToolbarItem | undefined {
    // @TODO clean this up / document why/how it works
    return this.items.find((item: any) => {
      const container: any = getClosestContainerNode(item);
      const focused = container.activeElement;
      const isEqualNode = focused?.isEqualNode(item);
      // Also match when focus is on a button inside a segmented control
      const isInsideSegmented = item.tagName?.toLowerCase() === 'ids-segmented-control'
        && item.contains(document.activeElement);
      return isEqualNode || isInsideSegmented;
    });
  }

  /**
   * @readonly
   * @returns {Array<ToolbarItem>} list of all available toolbar items present in all toolbar sections
   */
  get items(): Array<ToolbarItem> {
    const i: Array<ToolbarItem> = [];
    this.sections.forEach((section) => {
      // Pass along the More Actions button, if applicable
      if (section?.name === 'ids-toolbar-more-actions' && 'button' in section) {
        const btn = section.button;
        if (btn) i.push(btn as ToolbarItem);
      } else if ('items' in section) {
        i.push(...(section.items as Array<ToolbarItem>));
      }
    });
    return i;
  }

  /**
   * @readonly
   * @returns {Array<HTMLElement>} list of all available text nodes present in all toolbar sections
   */
  get textElems(): Array<HTMLElement> {
    const i: Array<HTMLElement> = [];
    this.sections.forEach((section) => {
      if (section?.name !== 'ids-toolbar-more-actions' && 'textElems' in section) {
        i.push(...section.textElems);
      }
    });
    return i;
  }

  /**
   * @readonly
   * @returns {Array<HTMLElement>} list of all available ids-separator nodes present in all toolbar sections
   */
  get separators(): Array<HTMLElement> {
    const sep: Array<HTMLElement> = [];
    this.sections.forEach((section) => {
      if (section?.name !== 'ids-toolbar-more-actions' && 'separators' in section) {
        sep.push(...section.separators);
      }
    });
    return sep;
  }

  /**
   * @readonly
   * @returns {Array<IdsToolbarSection | IdsToolbarMoreActions>} list of available sections within the toolbar
   */
  get sections(): Array<IdsToolbarSection | IdsToolbarMoreActions> {
    return [...this.children].filter((e) => e.matches('ids-toolbar-section, ids-toolbar-more-actions')) as Array<IdsToolbarSection | IdsToolbarMoreActions>;
  }

  /**
   * If true, sets the Toolbar mode to allow ALL items to have a usable tabIndex.
   * Default is false, which means one Toolbar element is focusable at a time.
   * @param {boolean | string} val sets the tabbable state of the toolbar
   */
  set tabbable(val: boolean | string) {
    const trueVal = stringToBool(val);

    if (trueVal) {
      this.setAttribute(attributes.TABBABLE, trueVal.toString());
    } else {
      this.removeAttribute(attributes.TABBABLE);
    }

    this.container?.classList[trueVal ? 'add' : 'remove'](attributes.TABBABLE);

    // Try to use a currently-focused element
    this.makeTabbable(this.focused);
  }

  /**
   * @returns {boolean} true if the toolbar is fully tabbable
   */
  get tabbable(): boolean {
    return stringToBool(this.getAttribute(attributes.TABBABLE));
  }

  /**
   * Set the type for toolbar
   * @param {string | null} value of toolbar type
   */
  set type(value: string | null) {
    let safeValue: any = null;
    if (typeof value === 'string') {
      safeValue = stripTags(value, '');
    }

    const currentValue = this.type;
    if (currentValue !== safeValue) {
      if (TOOLBAR_TYPES.includes(safeValue)) {
        this.setAttribute(attributes.TYPE, `${safeValue}`);
      } else {
        this.removeAttribute(attributes.TYPE);
        safeValue = null;
      }
      this.#setType(currentValue, safeValue);
    }
  }

  get type(): string | null {
    return this.getAttribute(attributes.TYPE);
  }

  /**
   * If set to number the container will have padding added (in pixels)
   * @param {string | number | null} value sets the padding to the container
   */
  set padding(value: string | number | null) {
    if (value !== null) {
      this.setAttribute(attributes.PADDING, value.toString());
      this.container?.style.setProperty('padding-bottom', `${value}px`);
    } else {
      this.removeAttribute(attributes.PADDING);
      this.container?.style.removeProperty('padding-bottom');
    }
  }

  get padding(): string | null {
    return this.getAttribute(attributes.PADDING);
  }

  /**
   * Set the toolbar type to each section
   * @private
   * @param {string|null} oldType the type class to remove
   * @param {string|null} newType the type class to add
   * @returns {void}
   */
  #setType(oldType: string | null, newType: string | null) {
    const cl = this.container?.classList;

    // Update CSS Class for main Toolbar type
    if (oldType) cl?.remove(`type-${oldType}`);
    if (newType) cl?.add(`type-${newType}`);

    // If using a "formatter" type, change the buttons/separators/etc to use the alternate style
    this.sections.forEach((s) => {
      s.setAttribute(attributes.TOOLBAR_TYPE, `${newType}`);
      if (s.tagName === 'IDS-TOOLBAR-MORE-ACTIONS') {
        const moreActions = s as IdsToolbarMoreActions;
        if (newType === 'formatter') {
          moreActions.colorVariant = FORMATTER_VARIANT;
        } else {
          moreActions.colorVariant = null;
        }
      }
    });
    [...this.items, ...this.separators].forEach((item) => {
      if (newType === 'formatter') {
        item.setAttribute(attributes.COLOR_VARIANT, FORMATTER_VARIANT);
      } else {
        const val = item.getAttribute(attributes.COLOR_VARIANT);
        if (val === FORMATTER_VARIANT) item.removeAttribute(attributes.COLOR_VARIANT);
      }
    });
  }

  /**
   * Gets the current item that should be used as the "tabbable" item
   * (item that receives focus when the toolbar overall is "focused").
   * @returns {HTMLElement | undefined} an element that currently has a usable tabIndex attribute
   */
  detectTabbable() {
    let tabbableItem;
    for (let i = 0; !tabbableItem && i < this.items.length; i++) {
      if (this.items[i].tabIndex > -1) {
        tabbableItem = this.items[i];
      }
    }
    return tabbableItem;
  }

  /**
   * Sets the tabbable state for toolbar items
   * @private
   * @param {HTMLElement} elem an element residing within the toolbar to make tabbable
   * @returns {void}
   */
  makeTabbable(elem = this.items[0]) {
    const isTabbable = this.tabbable;
    this.items.forEach((item: HTMLElement) => {
      const nonTabbableTargetIndex = elem.isEqualNode(item) ? 0 : -1;
      item.tabIndex = isTabbable ? 0 : nonTabbableTargetIndex;
    });
  }

  /**
   * Handles resize events by refreshing overflowed items
   * @private
   * @returns {void}
   */
  #resize(): void {
    this.refreshOverflowedItems();
  }

  /**
   * Triggers a `selected` event on a specified Toolbar item
   * @param {any} elem the specified Toolbar item
   * @param {boolean} [triggeredFromOverflow] if true, notifies the event handler that this
   *  `selected` event originated from the Overflow menu
   * @returns {void}
   */
  triggerSelectedEvent(elem: any, triggeredFromOverflow = false) {
    if (!elem) {
      return;
    }

    const detail: any = {
      elem,
      value: elem.value
    };

    if (elem.overflowTarget) {
      detail.elem = elem.overflowTarget;
      detail.value = elem.overflowTarget.value;
      detail.overflowMenuItem = elem;
      detail.triggeredFromOverflow = triggeredFromOverflow;
    }

    // Dispatch on targetElement only for overflow items, otherwise use elem
    const eventTarget = elem.overflowTarget ? this : elem;

    eventTarget.dispatchEvent(new CustomEvent('selected', {
      bubbles: true,
      detail
    }));
  }
}
